/**
 * appPermissions — pure gate helpers for application-lifecycle CTAs.
 *
 * Kept separate from `helpers/selectors.js` so the module has no
 * dependency on the legacy JWT-groups permission map. The intent is to
 * migrate onto the coord-side account/member model exclusively over
 * time; new gates land here first, existing ones move as they get
 * touched.
 *
 * canAddApplications({ currentUser, accountMember, projectId }) -> bool
 *
 * True iff any of:
 *   1. currentUser.userType === 'SYSTEM'
 *   2. accountMember.role === 'ACCOUNT_ADMIN'
 *   3. accountMember.canCreateApplications === true
 *      AND accountMember.projects[projectId].role === 'PROJECT_ADMIN'
 *
 * SYSTEM comes from the JWT (unchanged from the legacy gate).
 * ACCOUNT_ADMIN falls back to the JWT groups[] entry so operators keep
 * their button while the coord PR that adds member.role /
 * member.canCreateApplications is still in flight (see task motivation).
 * Everyone else needs the coord-side grant + a matching PROJECT_ADMIN
 * assignment for the currently-selected workspace.
 *
 * Any missing input returns false — the button stays hidden until we
 * have enough data to confirm the grant. Silent hide is the safe
 * default per the task's "undefined -> falsy is safe" contract.
 */
export function canAddApplications({
  currentUser,
  accountMember,
  projectId,
} = {}) {
  // 1) SYSTEM: JWT userType wins — never depends on the account-member
  // row, so support+ops can always add apps even before coord catches up.
  if (currentUser?.userType === "SYSTEM") return true;

  // 2) ACCOUNT_ADMIN: prefer the authoritative member.role from coord.
  // Fall back to the JWT groups[] entry so the button stays live while
  // the coord PR that adds member.role/canCreateApplications is still
  // merging separately.
  if (accountMember?.role === "ACCOUNT_ADMIN") return true;
  const jwtAccountAdmin =
    Array.isArray(currentUser?.groups) &&
    currentUser.groups.some(
      (g) => g?.userRole?.roleType === "ACCOUNT_ADMIN"
    );
  if (jwtAccountAdmin) return true;

  // 3) Delegated create — needs BOTH the per-account create-apps grant
  // AND a PROJECT_ADMIN role on the currently-selected workspace.
  if (!accountMember?.canCreateApplications) return false;
  if (!projectId) return false;
  const projects = Array.isArray(accountMember.projects)
    ? accountMember.projects
    : [];
  const grant = projects.find((p) => p && p.projectId === projectId);
  return grant?.role === "PROJECT_ADMIN";
}

/**
 * addApplicationsHelperText(gateInput) -> string | null
 *
 * When the button is HIDDEN, returns a short one-liner the empty-state
 * can render so the user knows exactly what to ask for. When the gate
 * says the user CAN add apps, returns null (caller renders nothing).
 *
 * Copy is intentionally action-oriented: it names the permission and
 * the person who can grant it, so the user has one clear next step.
 */
export function addApplicationsHelperText(gateInput = {}) {
  if (canAddApplications(gateInput)) return null;
  return "To add an application, ask an account admin to grant you the Create Applications permission.";
}
